import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import type { VmSpecs } from "kontrolEdge/components/Shared/VmSpecsCard";
import type { FwaasOverview } from "../overview/overview.types";
import type { FirewallRouting } from "../routing/routing.types";
import type { Reading } from "../readings/useFwaasReading";
import { MEMORY_PCT, type DeviceVerdict, type HealthStat } from "../attention/needsAttention";
import CardHeading from "../overview/CardHeading";
import { EMPTY, NO_READING, daysSince, formatMemoryMb, shortDate, shortTime } from "../overview/overviewFormat";
import { ALERT_TONE } from "../topology/alertTone";
import type { AlertLevel } from "../topology/serviceMap";

interface Stat {
    key: HealthStat;
    label: string;
    value: string;
    unit?: string;
    caption: string;
    /** What the number measures, on hover. */
    title?: string;
}

const noReading = (key: HealthStat, label: string): Stat => ({ key, label, value: EMPTY, caption: NO_READING });
const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

/** Days up; under a day, hours or minutes with the time, so a recent restart shows (CTX-1544). */
function uptimeStat(since: string, days: number, now: number): Stat {
    const base = { key: "uptime" as const, label: "Uptime" };
    if (days > 0) return { ...base, value: String(days), unit: days === 1 ? "day" : "days", caption: `Since ${shortDate(since)}` };
    const minutes = Math.max(0, Math.floor((now - Date.parse(since)) / 60_000));
    const caption = `Since ${shortDate(since)}, ${shortTime(since)}`;
    if (minutes < 60) return { ...base, value: String(minutes), unit: "min", caption };
    const hours = Math.floor(minutes / 60);
    return { ...base, value: String(hours), unit: hours === 1 ? "hour" : "hours", caption };
}

/**
 * The six numbers of the firewall's health, now (CTX-1208). Numbers only, with one
 * line of context each: the bars, peak marks and charts of earlier designs read as
 * too much (review, 2026-10-15).
 */
export function healthStats(overview: FwaasOverview, vm: VmSpecs | null, routing: Reading<FirewallRouting>, now = Date.now()): Stat[] {
    const { resources, uptime, ha } = overview;
    const vcpu = vm?.vcpu ?? null;
    const totalMb = resources?.memory.total_mb ?? vm?.memory_mb ?? null;
    const days = uptime ? daysSince(uptime.since, now) : null;
    const stats: Stat[] = [
        resources?.cpu.now == null
            ? noReading("cpu", "CPU")
            : {
                  key: "cpu",
                  label: "CPU",
                  value: String(resources.cpu.now),
                  unit: "%",
                  caption: [resources.cpu.peak_1h != null ? `Peak ${resources.cpu.peak_1h}%` : null, vcpu != null ? plural(vcpu, "vCPU") : null].filter(Boolean).join(" · "),
                  title: "Share of all vCPUs in use; the peak is the highest reading of the last hour.",
              },
        resources?.memory.now == null
            ? noReading("memory", "Memory")
            : {
                  key: "memory",
                  label: "Memory",
                  value: String(resources.memory.now),
                  unit: "%",
                  caption: [resources.memory.peak_1h != null ? `Peak ${resources.memory.peak_1h}%` : null, totalMb != null ? `of ${formatMemoryMb(totalMb)}` : null].filter(Boolean).join(" · "),
                  title: `Share of the VM's memory in use. From ${MEMORY_PCT.critical}%, the firewall may refuse new connections.`,
              },
        resources?.sessions.now == null
            ? noReading("sessions", "Sessions")
            : {
                  key: "sessions",
                  label: "Sessions",
                  value: resources.sessions.now.toLocaleString("en-US"),
                  caption: resources.sessions.peak_1h != null ? `Peak ${resources.sessions.peak_1h.toLocaleString("en-US")} in the last hour` : "",
                  title: "Connections passing through the firewall now.",
              },
        uptime == null || days == null ? noReading("uptime", "Uptime") : uptimeStat(uptime.since, days, now),
    ];
    if (ha == null) stats.push(noReading("ha", "HA"));
    else if (ha.mode === "standalone") stats.push({ key: "ha", label: "HA", value: "Standalone", caption: "One unit, no failover" });
    else {
        stats.push({
            key: "ha",
            label: "HA",
            value: `${ha.members_up ?? EMPTY}`,
            unit: `/${ha.members_total ?? EMPTY} up`,
            caption: [ha.in_sync == null ? null : ha.in_sync ? "In sync" : "Out of sync", ha.last_failover_at ? `failover ${shortDate(ha.last_failover_at)}` : null].filter(Boolean).join(" · "),
        });
    }
    const bgp = { key: "bgp" as const, label: "BGP" };
    if (routing === undefined) stats.push({ ...bgp, value: "…", caption: "" });
    else if (routing === null) stats.push(noReading("bgp", "BGP"));
    else if (routing.bgp.neighbors_total === 0) stats.push({ ...bgp, value: "Off", caption: `${plural(routing.routes.total, "route")}` });
    else stats.push({ ...bgp, value: String(routing.bgp.neighbors_up), unit: `/${routing.bgp.neighbors_total} up`, caption: plural(routing.routes.total, "route") });
    return stats;
}

/**
 * One number with its label and caption. The number a step smaller and lighter than it
 * was (CTX-1527): at 24 px semibold, six of them — "Standalone" above all — were the
 * loudest thing on the page (review of 27-oct). Out of range it takes its pill's
 * color, unit and all, so the pill's reason is found at a glance (CTX-1544).
 */
function StatView({ stat, alert }: { stat: Stat; alert?: AlertLevel }) {
    return (
        <div className="min-w-0" title={stat.title}>
            <div className="text-xs text-semantic-text-secondary truncate">{stat.label}</div>
            <div className={`text-xl font-medium leading-tight ${alert ? ALERT_TONE[alert].text : ""}`}>
                {stat.value}
                {stat.unit ? <span className={alert ? "ml-1 text-sm font-normal" : "ml-1 text-sm font-normal text-semantic-text-secondary"}>{stat.unit}</span> : null}
            </div>
            {/* One line each, so the rows stay aligned at any width; the whole caption on hover (CTX-1391). */}
            <div className="text-xs text-semantic-text-secondary truncate" title={stat.caption || undefined}>
                {stat.caption || "\u00a0"}
            </div>
        </div>
    );
}

/**
 * The grid of the six numbers, read or on their way, so they land where their
 * placeholders were. Rows 24 px apart, so two rows of three read as two (review of 27-oct).
 */
const STATS_GRID = "grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-6";

const VERDICT_VARIANT: Record<DeviceVerdict["tone"], "success" | "warning" | "error" | "neutral"> = {
    healthy: "success",
    attention: "warning",
    critical: "error",
    unread: "neutral",
};

export default function DeviceHealthCard({
    overview,
    vm,
    routing,
    verdict,
}: {
    /** `null` while the first reading is on its way. */
    overview: FwaasOverview | null;
    vm: VmSpecs | null;
    routing: Reading<FirewallRouting>;
    /** From `deviceVerdict`: none while reading, or when part was read and that part is fine. */
    verdict: DeviceVerdict | null;
}) {
    const tag = overview ? verdict : null;
    return (
        <GenericCard isCollapsible={false} variant="primary" shadow="sm">
            <section className="p-5" aria-label="Device health" aria-busy={overview === null}>
                {/* 20 px under the header: the first row of numbers starts level with the first observation beside it. */}
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 mb-5">
                    <CardHeading title="Device health" subtitle="Now, with the peak of the last hour" />
                    {tag && (
                        // Every reason on hover, when the pill counts more than one.
                        <span className="shrink-0 whitespace-nowrap" title={tag.title}>
                            <GenericTag label={tag.label} variant={VERDICT_VARIANT[tag.tone]} size="small" />
                        </span>
                    )}
                </div>
                {overview === null ? (
                    <div className={STATS_GRID}>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <SkeletonBlock key={i} h={52} decorative />
                        ))}
                    </div>
                ) : (
                    <div className={STATS_GRID}>
                        {healthStats(overview, vm, routing).map((stat) => (
                            <StatView key={stat.key} stat={stat} alert={verdict?.stats[stat.key]} />
                        ))}
                    </div>
                )}
            </section>
        </GenericCard>
    );
}
